import { useEffect, useState } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown, Eye, Send, Copy, Type, AlignLeft, Image as ImageIcon, MousePointerClick, Package, Minus, Save, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errMsg, fmtDate } from "../lib/api";
import { Card, Title, Field, ImageInput, Badge, act } from "./kit";

const ADD = [["heading", "Nadpis", Type], ["text", "Text", AlignLeft], ["image", "Obrázek", ImageIcon], ["button", "Tlačítko", MousePointerClick], ["product", "Produkt", Package], ["divider", "Oddělovač", Minus]];
const STATUS = { draft: ["Koncept", "gray"], sending: ["Odesílá se", "sun"], sent: ["Odesláno", "teal"], partial: ["Odesláno s chybami", "pink"] };

function ProductPick({ value, onChange, products }) {
  const [q, setQ] = useState("");
  const sel = products.find((p) => p.id === value);
  const list = q ? products.filter((p) => p.name.toLowerCase().includes(q.toLowerCase())).slice(0, 8) : [];
  return (
    <div>
      {sel && <p className="text-sm font-bold mb-2" data-testid="block-product-selected">Vybráno: {sel.name}</p>}
      <input className="field" placeholder="Hledat produkt podle názvu…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="block-product-search" />
      {list.length > 0 && (
        <div className="mt-1 rounded-xl border border-[#f1e4ea] divide-y bg-white">
          {list.map((p) => <button key={p.id} type="button" onClick={() => { onChange(p.id); setQ(""); }} className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--blush)]" data-testid={`block-product-option-${p.slug}`}>{p.name}</button>)}
        </div>
      )}
    </div>
  );
}

function Block({ b, set, products }) {
  if (b.type === "heading") return <input className="field font-bold" placeholder="Nadpis" value={b.text || ""} onChange={(e) => set({ text: e.target.value })} data-testid="block-heading-input" />;
  if (b.type === "text") return <textarea rows={4} className="field" placeholder="Text (prázdný řádek = nový odstavec)" value={b.text || ""} onChange={(e) => set({ text: e.target.value })} data-testid="block-text-input" />;
  if (b.type === "image") return <div className="space-y-2"><ImageInput value={b.src} onChange={(v) => set({ src: v })} testid="block-image" /><input className="field" placeholder="Odkaz po kliknutí (nepovinné)" value={b.link || ""} onChange={(e) => set({ link: e.target.value })} data-testid="block-image-link" /></div>;
  if (b.type === "button") return <div className="grid sm:grid-cols-2 gap-2"><input className="field" placeholder="Text tlačítka" value={b.label || ""} onChange={(e) => set({ label: e.target.value })} data-testid="block-button-label" /><input className="field" placeholder="https://www.materialyproskolky.com/eshop" value={b.url || ""} onChange={(e) => set({ url: e.target.value })} data-testid="block-button-url" /></div>;
  if (b.type === "product") return <ProductPick value={b.product_id} onChange={(v) => set({ product_id: v })} products={products} />;
  return <hr className="border-dashed border-[var(--pink)]" />;
}

function Editor({ id, back }) {
  const [c, setC] = useState(null);
  const [products, setProducts] = useState([]);
  const [html, setHtml] = useState("");
  useEffect(() => {
    let live = true;
    adminApi.get(`/campaigns/${id}`).then((r) => live && setC(r.data));
    adminApi.get("/products").then((r) => live && setProducts(r.data));
    return () => {
      live = false;
    };
  }, [id]);
  useEffect(() => {
    if (!c) return undefined;
    const t = setTimeout(() => adminApi.post("/campaigns/preview", c).then((r) => setHtml(r.data.html)).catch(() => {}), 500);
    return () => clearTimeout(t);
  }, [c]);
  useEffect(() => {
    if (!c || c.status !== "sending") return undefined;
    const t = setInterval(() => adminApi.get(`/campaigns/${id}`).then((r) => setC(r.data)), 3000);
    return () => clearInterval(t);
  }, [c?.status, id]); // eslint-disable-line
  if (!c) return null;
  const draft = c.status === "draft";
  const setB = (i, v) => setC({ ...c, blocks: c.blocks.map((b, k) => (k === i ? { ...b, ...v } : b)) });
  const move = (i, d) => {
    const bl = [...c.blocks];
    [bl[i], bl[i + d]] = [bl[i + d], bl[i]];
    setC({ ...c, blocks: bl });
  };
  const save = () => act(async () => setC((await adminApi.put(`/campaigns/${id}`, c)).data), "Kampaň uložena");
  const call = async (path, okMsg) => {
    try {
      if (draft) await adminApi.put(`/campaigns/${id}`, c);
      const { data } = await adminApi.post(`/campaigns/${id}/${path}`);
      toast.success(data.message || okMsg);
      setC((await adminApi.get(`/campaigns/${id}`)).data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const sendAll = () => {
    if (window.confirm(`Opravdu odeslat newsletter „${c.subject}“ všem aktivním odběratelům? Akci nelze vrátit.`)) call("send", "Odesílání spuštěno");
  };
  return (
    <div data-testid="campaign-editor">
      <Title right={<div className="flex flex-wrap gap-2">
        <button onClick={back} className="btn-ghost !py-2" data-testid="campaign-back-btn"><ArrowLeft className="w-4 h-4" /> Zpět</button>
        {draft && <button onClick={save} className="btn-ghost !py-2" data-testid="campaign-save-btn"><Save className="w-4 h-4" /> Uložit</button>}
        {!["sent", "partial"].includes(c.status) && <button onClick={() => call("test", "Test odeslán")} className="btn-ghost !py-2" data-testid="campaign-test-btn"><Eye className="w-4 h-4" /> Poslat test mně</button>}
        {!["sent", "partial"].includes(c.status) && !c.running && <button onClick={sendAll} className="btn-pink !py-2" data-testid="campaign-send-btn"><Send className="w-4 h-4" /> {c.status === "sending" ? "Pokračovat v odesílání" : "Odeslat všem"}</button>}
      </div>}>{c.subject || "Nová kampaň"}</Title>
      {c.status !== "draft" && (
        <Card className="mb-4" data-testid="campaign-progress">
          <div className="flex justify-between text-sm font-bold mb-2"><span>{c.running ? <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Odesílá se…</span> : STATUS[c.status][0]}</span><span>{c.sent} / {c.total} odesláno{c.failed ? ` · ${c.failed} chyb` : ""}</span></div>
          <div className="h-3 rounded-full bg-[var(--blush)] overflow-hidden"><div className="h-full bg-[var(--pink)] transition-[width] duration-700" style={{ width: `${c.total ? Math.min(100, ((c.sent + c.failed) / c.total) * 100) : 0}%` }} /></div>
          {c.error && !c.running && <p className="text-xs text-[var(--pink-dark)] mt-2">{c.error}</p>}
        </Card>
      )}
      <div className="grid xl:grid-cols-[1fr_600px] gap-4">
        <div className="space-y-3">
          <Card className="space-y-3">
            <Field label="Předmět e-mailu"><input className="field" disabled={!draft} value={c.subject} onChange={(e) => setC({ ...c, subject: e.target.value })} data-testid="campaign-subject-input" /></Field>
            <Field label="Náhledový text (zobrazí se v seznamu e-mailů)"><input className="field" disabled={!draft} value={c.preheader} onChange={(e) => setC({ ...c, preheader: e.target.value })} data-testid="campaign-preheader-input" /></Field>
            <Field label="Hlavní nadpis"><input className="field" disabled={!draft} value={c.title} onChange={(e) => setC({ ...c, title: e.target.value })} data-testid="campaign-title-input" /></Field>
          </Card>
          {c.blocks.map((b, i) => (
            <Card key={i} className="!p-4" data-testid={`campaign-block-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold uppercase tracking-wider text-[var(--muted)]">{ADD.find((a) => a[0] === b.type)?.[1]}</span>
                {draft && <div className="flex gap-1">
                  <button disabled={i === 0} onClick={() => move(i, -1)} className="w-8 h-8 grid place-items-center rounded-full hover:bg-[var(--blush)] disabled:opacity-30" data-testid={`block-up-${i}`}><ArrowUp className="w-4 h-4" /></button>
                  <button disabled={i === c.blocks.length - 1} onClick={() => move(i, 1)} className="w-8 h-8 grid place-items-center rounded-full hover:bg-[var(--blush)] disabled:opacity-30" data-testid={`block-down-${i}`}><ArrowDown className="w-4 h-4" /></button>
                  <button onClick={() => setC({ ...c, blocks: c.blocks.filter((_, k) => k !== i) })} className="w-8 h-8 grid place-items-center rounded-full hover:bg-[var(--blush)]" data-testid={`block-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
                </div>}
              </div>
              <fieldset disabled={!draft}><Block b={b} set={(v) => setB(i, v)} products={products} /></fieldset>
            </Card>
          ))}
          {draft && (
            <Card className="!p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-[var(--muted)] mb-2">Přidat blok</p>
              <div className="flex flex-wrap gap-2">
                {ADD.map(([t, l, I]) => <button key={t} onClick={() => setC({ ...c, blocks: [...c.blocks, { type: t }] })} className="px-4 py-2 rounded-full bg-[var(--blush)] hover:bg-[var(--pink-soft)] text-sm font-semibold flex items-center gap-2 transition-colors" data-testid={`add-block-${t}`}><I className="w-4 h-4" /> {l}</button>)}
              </div>
            </Card>
          )}
        </div>
        <Card className="!p-3 xl:sticky xl:top-4 self-start">
          <p className="text-xs font-bold uppercase tracking-wider text-[var(--muted)] mb-2 px-1">Živý náhled</p>
          <iframe title="náhled" srcDoc={html} sandbox="" className="w-full h-[75vh] rounded-2xl border border-[#f1e4ea] bg-white" data-testid="campaign-preview" />
        </Card>
      </div>
    </div>
  );
}

export default function Campaigns() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => adminApi.get("/campaigns").then((r) => setData(r.data));
  useEffect(() => {
    load();
  }, []);
  if (open) return <Editor id={open} back={() => { setOpen(null); load(); }} />;
  if (!data) return null;
  const create = async () => {
    const { data: c } = await adminApi.post("/campaigns", { subject: "", title: "", blocks: [{ type: "heading", text: "" }, { type: "text", text: "" }] });
    setOpen(c.id);
  };
  return (
    <div data-testid="admin-campaigns">
      <Title right={<button onClick={create} className="btn-pink" data-testid="campaign-new-btn"><Plus className="w-4 h-4" /> Nová kampaň</button>}>Newsletter kampaně</Title>
      <p className="text-sm text-[var(--muted)] -mt-2 mb-4">Sestavte e-mail z bloků (nadpis, text, obrázek, tlačítko, produkt) a jedním tlačítkem ho pošlete všem <b>{data.active}</b> aktivním odběratelům přes Resend. Každý e-mail obsahuje odkaz pro odhlášení.</p>
      {!data.own_resend && <Card className="mb-4 !bg-[var(--sun-soft)] text-sm" data-testid="campaign-resend-warning">Pro hromadné rozesílání zapněte v záložce <b>API & nastavení</b> vlastní Resend účet (API klíč + adresa na ověřené doméně). Testovací e-mail sobě můžete poslat i bez něj.</Card>}
      <div className="space-y-2">
        {data.items.length === 0 && <Card className="text-sm text-[var(--muted)]">Zatím žádná kampaň.</Card>}
        {data.items.map((c) => (
          <Card key={c.id} className="!p-4 flex flex-wrap items-center gap-3" data-testid={`campaign-row-${c.id}`}>
            <button onClick={() => setOpen(c.id)} className="flex-1 min-w-[200px] text-left font-bold hover:text-[var(--pink-dark)]">{c.subject || "(bez předmětu)"}</button>
            <Badge tone={STATUS[c.status][1]}>{STATUS[c.status][0]}</Badge>
            {c.status !== "draft" && <span className="text-sm text-[var(--muted)]">{c.sent}/{c.total}</span>}
            <span className="text-sm text-[var(--muted)]">{fmtDate(c.sent_at || c.created_at)}</span>
            <button onClick={async () => { await adminApi.post(`/campaigns/${c.id}/copy`); load(); }} title="Kopírovat" className="w-8 h-8 grid place-items-center rounded-full hover:bg-[var(--blush)]" data-testid={`campaign-copy-${c.id}`}><Copy className="w-4 h-4" /></button>
            <button onClick={async () => { if (window.confirm("Smazat kampaň?")) { try { await adminApi.delete(`/campaigns/${c.id}`); load(); } catch (e) { toast.error(errMsg(e)); } } }} title="Smazat" className="w-8 h-8 grid place-items-center rounded-full hover:bg-[var(--blush)]" data-testid={`campaign-delete-${c.id}`}><Trash2 className="w-4 h-4" /></button>
          </Card>
        ))}
      </div>
    </div>
  );
}
